import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { useFormik, useFormikContext } from 'formik';
import { DateTime } from 'luxon';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import * as yup from 'yup';
import AccordionCancellationPracticeForm, { CancellationPracticeVM } from './form';

interface CancellationPracticeProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
}

const AccordionCancellationPracticeComponent = ({
  loadUpdateDate,
}: CancellationPracticeProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const ref = useRef<HTMLDivElement | null>(null);

  const updateData = useHttpClient(`${__PRACTICE_API_URL__}/pratiche/sospensione-inizio-pratica`, {
    options: { method: 'POST' },
  });

  const params = useParams();

  const onSubmit = useEventCallback((values: CancellationPracticeVM) => {
    const date = values.suspensionDate?.toFormat('yyyy-MM-dd') || '';

    const hours = values.time?.toFormat('THHmmssSSS') || '';

    const timestampInizioSospensione =
      DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd THHmmssSSS') || undefined;

    (
      updateData({
        data: {
          key: {
            codiceEnte: params?.codiceEnte || undefined,
            tipoRegistro: params?.tipoRegistro || undefined,
            idAnnoPratica: params?.idAnnoPratica
              ? Number.parseInt(params?.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params?.progressivoPratica
              ? Number.parseInt(params?.progressivoPratica, 10)
              : undefined,
          },
          tipoPratica: params?.tipoPratica || undefined,
          sospensioneVO: {
            timestampInizioSospensione,
            noteSospensione: values.reason || undefined,
          },
        },
      }) as ResponsePromise<void>
    ).then(async () => {
      toast.success('Operazione eseguita con successo');

      // aggiornamento stato pratica
      formContextPage.setFieldValue('practice', {
        ...(formContextPage.values.practice || {}),
        codiceStatoPratica: '5',
        tipoOperazione: '50',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }
    });
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormik<CancellationPracticeVM>({
    initialValues: {
      suspensionDate: DateTime.now(),
      time: DateTime.now(),
      reason: '',
    },
    validationSchema: yup.object().shape({
      suspensionDate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      time: yup.date().typeError('Data non valida').nullable().required('Il campo è obbligatorio'),
      reason: yup.string().nullable().required('Il campo è obbligatorio'),
    }),
    onSubmit,
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-cancellation-practice-content"
          id="panel-cancellation-practice-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Registra Sospensione Pratica</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionCancellationPracticeForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            errors={formContext.errors}
            touched={formContext.touched}
          />
          <div className="pmi-align-right pmi-pt-2">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => formContext.handleSubmit()}
              id="confirm"
            >
              Conferma
            </PmiButton>
          </div>
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

AccordionCancellationPracticeComponent.displayName = 'Accordion End Practice component';

const AccordionCancellationPractice = memo(AccordionCancellationPracticeComponent);

export default AccordionCancellationPractice;
